import {
  Stack,
  styled,
  SxProps,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  TypographyProps,
} from '@mui/material';
import { forwardRef, PropsWithChildren } from 'react';
import * as React from 'react';

import { COLORS } from '@/styles/tokens/colors';

import { HeaderCellTypography } from '../components/HeaderCellTypography';
import { HEADER_BACKGROUND_COLOR, ROW_BORDER_STYLE } from '../constants';

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: HEADER_BACKGROUND_COLOR,
    color: theme.palette.common.white,
    padding: '12px',
    ':not(:last-of-type)': {
      borderRight: `solid white 1px`,
    },
  },
}));

export const StyledTableCell = styled(TableCell)(({ align, theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    border: 'none',
    padding: '12px',
    textAlign: align,
    '& .MuiTypography-title-s, & .MuiTypography-body-s': {
      ...theme.typography['body-xs'],
      color: COLORS.TEXT.PRIMARY,
    },
  },
}));

export interface DisplayTableColumn {
  headerName: React.ReactNode;
  headerIconAfter?: React.ReactNode;
  width?: number | string;
  flex?: number;
  align?: TableCellProps['align'];
}

export interface DisplayTableProps
  extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'standard' | 'plain';
  containerSx?: SxProps<Theme>;
  tableSx?: SxProps<Theme>;
  columns: DisplayTableColumn[];
  Footer?: React.ReactElement;
  header?: React.ReactNode;
  headerTypographyVariant?: TypographyProps['variant'];
  hideTableHead?: boolean;
  /** Show the top border of the first body row and the bottom border of the last body row. */
  showOuterRowBorders?: boolean;
}

type DisplayTablePropsWithChildren = PropsWithChildren<DisplayTableProps>;

function DisplayTableInner(
  props: DisplayTablePropsWithChildren,
  ref: React.ForwardedRef<HTMLTableElement>
) {
  const {
    children,
    variant = 'standard',
    containerSx,
    tableSx,
    columns,
    Footer,
    header,
    headerTypographyVariant = variant === 'plain' ? 'title-xs' : undefined,
    hideTableHead = false,
    showOuterRowBorders = false,
    ...rest
  } = props;
  return (
    <>
      {header}
      <TableContainer sx={containerSx} ref={ref} {...rest}>
        <Table
          sx={[
            variant === 'plain'
              ? {
                  '&& .MuiTableCell-head': {
                    backgroundColor: 'transparent',
                    color: COLORS.TEXT.PRIMARY,
                    borderRight: 0,
                    borderBottom: `2px solid ${COLORS.GRAY[500]}`,
                    '& .MuiTypography-root': { color: 'inherit', margin: 0 },
                  },
                  '&& .MuiTableCell-body': {
                    color: COLORS.TEXT.PRIMARY,
                    borderBottom: `1px solid ${COLORS.GRAY[200]}`,
                    '& .MuiTypography-root': { color: 'inherit' },
                  },
                }
              : {},
            ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          ]}
        >
          {!hideTableHead && (
            <TableHead>
              <TableRow>
                {columns.map(
                  ({ headerName, headerIconAfter, width, flex, align }, i) => (
                    <StyledHeaderCell
                      key={i}
                      sx={{ width, flex }}
                      align={align}
                    >
                      {headerIconAfter ? (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <HeaderCellTypography
                            sx={{ paddingX: 0, marginX: 1 }}
                            variant={headerTypographyVariant}
                          >
                            {headerName}
                          </HeaderCellTypography>
                          {headerIconAfter}
                        </Stack>
                      ) : (
                        <HeaderCellTypography
                          sx={{ paddingX: 0, marginX: 1 }}
                          variant={headerTypographyVariant}
                        >
                          {headerName}
                        </HeaderCellTypography>
                      )}
                    </StyledHeaderCell>
                  )
                )}
              </TableRow>
            </TableHead>
          )}
          <TableBody
            sx={
              showOuterRowBorders
                ? {
                    '& > tr:first-of-type': { borderTop: ROW_BORDER_STYLE },
                    '& > tr:last-of-type': { borderBottom: ROW_BORDER_STYLE },
                  }
                : undefined
            }
          >
            {children}
          </TableBody>
          {Footer}
        </Table>
      </TableContainer>
    </>
  );
}

export const DisplayTable = forwardRef<
  HTMLTableElement,
  DisplayTablePropsWithChildren
>(DisplayTableInner);
